import { ArrowRight, Check, Minus } from "lucide-react"
import type { Metadata } from "next"
import Link from "next/link"

export const metadata: Metadata = {
  title: "Pricing",
  description: "Resolto design-partner, standard, advanced, and focused launch pricing.",
}

const plans = [
  {
    name: "Design-partner pilot",
    price: "$500–$1,000",
    cadence: "per month",
    setup: "$1,000–$3,000 setup",
    scope: [
      "One app",
      "Slack intake",
      "One knowledge source",
      "One read-only connection",
      "Up to five playbooks",
    ],
    cta: "Discuss a pilot",
  },
  {
    name: "Standard production",
    price: "$1,250",
    cadence: "per month",
    setup: "Setup from $2,500",
    scope: [
      "One primary application",
      "Investigation workflow",
      "Ticketing integration",
      "Up to 15 playbooks",
      "ROI reporting",
    ],
    cta: "Discuss standard",
  },
  {
    name: "Advanced",
    price: "$2,000–$3,500",
    cadence: "per month",
    setup: "Setup quoted",
    scope: [
      "Multiple systems",
      "More playbooks",
      "Stricter controls",
      "Higher support boundary",
      "Manual provisioning",
    ],
    cta: "Discuss advanced",
  },
]

export default function PricingPage() {
  return (
    <main id="main-content">
      <section className="marketing-page-hero pricing-hero">
        <div className="marketing-container">
          <p className="section-kicker">Pricing</p>
          <h1>Price the technical time saved—not chatbot messages.</h1>
          <p>
            Resolto starts with a paid, tightly scoped implementation. There is no permanent free
            production plan and no hidden promise of unlimited custom integration work.
          </p>
        </div>
      </section>
      <section className="marketing-section pricing-section">
        <div className="marketing-container pricing-table">
          {plans.map((plan) => (
            <article key={plan.name}>
              <div className="plan-heading">
                <h2>{plan.name}</h2>
                <div>
                  <strong>{plan.price}</strong>
                  <span>{plan.cadence}</span>
                </div>
                <p>{plan.setup}</p>
              </div>
              <ul>
                {plan.scope.map((item) => (
                  <li key={item}>
                    <Check aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link className="button button-secondary" href="/contact">
                {plan.cta}
                <ArrowRight aria-hidden="true" />
              </Link>
            </article>
          ))}
        </div>
        <div className="marketing-container focused-offer">
          <div>
            <span>Recommended focused launch</span>
            <h2>One application. One database. One error source. Five playbooks.</h2>
            <p>A strict support boundary makes the first implementation measurable and reusable.</p>
          </div>
          <div>
            <strong>$1,000–$1,500</strong>
            <span>per month</span>
            <p>$2,500–$5,000 setup</p>
            <Link className="button button-primary" href="/contact">
              Scope the launch
            </Link>
          </div>
        </div>
      </section>
      <section className="marketing-section pricing-principles">
        <div className="marketing-container">
          <h2>Commercial boundaries</h2>
          <div>
            <p>
              <Check aria-hidden="true" />
              Setup fees reflect real permissions, connector, and playbook work.
            </p>
            <p>
              <Check aria-hidden="true" />
              Annual commitments follow proven value, not the first demo.
            </p>
            <p>
              <Check aria-hidden="true" />
              Usage and direct-support boundaries remain visible before limits.
            </p>
            <p>
              <Minus aria-hidden="true" />
              Bespoke, non-reusable integrations are rejected or priced explicitly.
            </p>
          </div>
        </div>
      </section>
    </main>
  )
}
